import { useEffect, useRef, useState, type CSSProperties } from "react";

import { useT } from "../i18n";
import type { MatchProfile } from "./types";

const PLAY_MS = 3400;
const REDUCED_MS = 400;

function prefersReduced(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function FlameSend({
  profile,
  onComplete,
  settled = false,
}: {
  profile: MatchProfile;
  onComplete: () => void;
  /** Already finished — keep the last frame, do not restart the timer. */
  settled?: boolean;
}) {
  const t = useT();
  const root = useRef<HTMLDivElement>(null);
  const done = useRef(settled);
  const timer = useRef(0);
  const [reduced] = useState(prefersReduced);

  const finish = () => {
    if (done.current) return;
    done.current = true;
    window.clearTimeout(timer.current);
    onComplete();
  };

  useEffect(() => {
    if (settled) {
      done.current = true;
      return;
    }
    done.current = false;
    root.current?.focus();
    timer.current = window.setTimeout(finish, reduced ? REDUCED_MS : PLAY_MS);
    return () => window.clearTimeout(timer.current);
    // finish is stable for this mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id, reduced, settled]);

  return (
    <div
      className={`match-flame-send${reduced ? " is-reduced" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="match-flame-send-title"
      onClick={finish}
      onKeyDown={(e) => {
        if (e.key === "Escape" || e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          finish();
        }
      }}
      tabIndex={0}
      ref={root}
    >
      <div className="match-flame-send__veil" aria-hidden />
      <div className="match-flame-send__stage" aria-hidden>
        <div className="match-flame-send__bloom" />
        <div className="match-flame-send__sparks">
          {Array.from({ length: 10 }, (_, s) => (
            <i key={s} className="match-flame-send__spark" style={{ "--s": s } as CSSProperties} />
          ))}
        </div>
        <div className="match-flame-send__token">
          <i className="match-flame-send__wick" />
        </div>
        <div className="match-flame-send__face">
          <span>{profile.firstName.charAt(0)}</span>
          {profile.photoUrl ? (
            <img
              src={profile.photoUrl}
              alt=""
              decoding="async"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : null}
        </div>
      </div>
      <div className="match-flame-send__copy">
        <p id="match-flame-send-title" className="match-flame-send__title">
          {t("match.flameBurstTitle", { name: profile.firstName })}
        </p>
        <p className="match-flame-send__hint">{t("match.packSkip")}</p>
      </div>
    </div>
  );
}
